import type {
  AntiBandingMode,
  AutoFocusMode,
  AutoWhitebalanceMode,
  ImgResizeMode,
} from "@/core/cameraApi";
import { tKey, type I18nKey } from "@/core/i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface SelectParameterProps {
  value: string | null;
  options: string[];
  label: string;
  disabled?: boolean;
  onValueChange: (value: string) => void;
}

type SensorOption = AutoFocusMode | AutoWhitebalanceMode | AntiBandingMode | ImgResizeMode;

/** Labels for the option values the camera API is known to send. */
const OPTION_LABELS = {
  OFF: tKey("capture:sensorOption.OFF"),
  AUTO: tKey("capture:sensorOption.AUTO"),
  MACRO: tKey("capture:sensorOption.MACRO"),
  CONTINUOUS_VIDEO: tKey("capture:sensorOption.CONTINUOUS_VIDEO"),
  CONTINUOUS_PICTURE: tKey("capture:sensorOption.CONTINUOUS_PICTURE"),
  EDOF: tKey("capture:sensorOption.EDOF"),
  INCANDESCENT: tKey("capture:sensorOption.INCANDESCENT"),
  FLUORESCENT: tKey("capture:sensorOption.FLUORESCENT"),
  WARM_FLUORESCENT: tKey("capture:sensorOption.WARM_FLUORESCENT"),
  DAYLIGHT: tKey("capture:sensorOption.DAYLIGHT"),
  CLOUDY_DAYLIGHT: tKey("capture:sensorOption.CLOUDY_DAYLIGHT"),
  TWILIGHT: tKey("capture:sensorOption.TWILIGHT"),
  SHADE: tKey("capture:sensorOption.SHADE"),
  MAINS_50_HZ: tKey("capture:sensorOption.MAINS_50_HZ"),
  MAINS_60_HZ: tKey("capture:sensorOption.MAINS_60_HZ"),
  CROP: tKey("capture:sensorOption.CROP"),
  STRETCH: tKey("capture:sensorOption.STRETCH"),
  LETTERBOX: tKey("capture:sensorOption.LETTERBOX"),
} satisfies Record<SensorOption, I18nKey<"capture">>;

const isKnownOption = (option: string): option is SensorOption =>
  Object.hasOwn(OPTION_LABELS, option);

// Fallback for a value newer firmware sends that has no label yet.
const formatOption = (option: string) =>
  option
    .toLowerCase()
    .split("_")
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");

export const SelectParameter = ({
  value,
  options,
  label,
  disabled,
  onValueChange,
}: SelectParameterProps) => {
  const { t } = useTranslation(["capture"]);

  if (options.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 text-xs leading-4">
      <Info className="size-4 shrink-0 text-foreground/40" />
      <span className="flex-1 truncate text-foreground/90">{label}</span>
      <Select
        value={value ?? undefined}
        disabled={disabled}
        onValueChange={(next) => {
          if (typeof next === "string") {
            onValueChange(next);
          }
        }}
      >
        <SelectTrigger size="sm" className="h-8 min-w-40 text-xs">
          <SelectValue placeholder="-" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {isKnownOption(option)
                ? t(OPTION_LABELS[option])
                : formatOption(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
